import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { Loader2 } from 'lucide-react'
import { useCreateStorageVolume } from '@/services/storage'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { Spinner } from '@/components/spinner'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { StorageTypeId, STORAGE_TYPES } from '@/constants/storageTypes'
import { WebrpcError } from '@/services/inference.gen'

type CreateSimpleStorageProps = {
  onSuccess: () => void
}

export const CreateSimpleStorage = ({
  onSuccess,
}: CreateSimpleStorageProps) => {
  const {
    data: activeOrgProfile,
    isLoading: isLoadingActiveOrgProfile,
    isError: isErrorActiveOrgProfile,
  } = useActiveOrgProfile()

  const storageSizeLimit =
    activeOrgProfile?.org?.metadata?.limits?.org_per_user_storage_gb
  const storageCountLimit =
    activeOrgProfile?.org?.metadata?.limits?.org_per_user_storage_count

  const FormSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    size_gb: z
      .number()
      .min(1, 'Size must be at least 1GB')
      .max(storageSizeLimit || 0, `Size cannot exceed ${storageSizeLimit}GB`),
    is_public: z.boolean().default(false),
    type_id: z.number().default(StorageTypeId.S3),
  })

  type FormValues = z.infer<typeof FormSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      size_gb: storageSizeLimit,
      is_public: false,
      type_id: StorageTypeId.S3,
    },
  })

  const { mutateAsync: createStorageVolumeMutate, isPending } =
    useCreateStorageVolume()

  const onSubmit = async (data: FormValues) => {
    try {
      await createStorageVolumeMutate({
        name: data.name,
        size_gb: data.size_gb,
        read_only: false,
        is_public: data.is_public,
        google_drive_metadata: [],
        type_id: data.type_id,
      })

      form.reset()
      onSuccess()
      toast.success('Storage volume created successfully')
    } catch (error) {
      console.error(error)
      if (error instanceof WebrpcError) {
        toast.error(JSON.stringify(error))
      } else {
        toast.error('Failed to create storage volume')
      }
    }
  }

  if (isLoadingActiveOrgProfile) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Spinner />
      </div>
    )
  }

  if (isErrorActiveOrgProfile) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <p className="text-sm text-destructive-foreground">
          Failed to fetch organization profile. Please contact support.
        </p>
      </div>
    )
  }

  if (!storageSizeLimit || !storageCountLimit) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <p className="text-sm text-destructive-foreground">
          No storage limits found. Please contact support.
        </p>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-5"
        id="simple-storage-form"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="my-storage" {...field} />
                </FormControl>
                <FormDescription className="text-xs">
                  A unique name for your storage
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="size_gb"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Size (GB)</FormLabel>
                  <Badge variant="outline" className="text-xs font-normal">
                    Max: {storageSizeLimit}GB
                  </Badge>
                </div>
                <FormControl>
                  <Input
                    type="number"
                    placeholder={storageSizeLimit.toString()}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <div className="mt-1.5">
                  <Progress
                    value={(form.watch('size_gb') / storageSizeLimit) * 100}
                    className="h-1.5"
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="type_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Storage Type</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={(value) => field.onChange(Number(value))}
                  defaultValue={field.value.toString()}
                  className="grid grid-cols-3 gap-4"
                >
                  {STORAGE_TYPES.map((type) => (
                    <div key={type.id}>
                      <RadioGroupItem
                        value={type.id.toString()}
                        id={`type-${type.id}`}
                        className="peer sr-only"
                      />
                      <label
                        htmlFor={`type-${type.id}`}
                        className="flex flex-col items-start justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium leading-none">
                            {type.label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {type.description}
                          </p>
                        </div>
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_public"
          render={({ field }) => (
            <FormItem
              className={cn(
                'flex flex-row items-center justify-between rounded-lg border p-3',
                field.value && 'bg-primary/10 border-primary/20'
              )}
            >
              <div className="space-y-0.5">
                <FormLabel>Organization-wide access</FormLabel>
                <FormDescription className="text-xs">
                  Make storage readable by all users in your organization
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="bg-muted ring-ring ring rounded-md p-3 text-sm flex items-start gap-2">
          <InfoCircledIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">Organization Limits:</span>{' '}
            {storageCountLimit} active storage volumes, max {storageSizeLimit}GB
            per volume
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button disabled={isPending} type="submit">
            {isPending ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Creating...
              </span>
            ) : (
              'Create Storage'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
